import { useEffect, useRef, useState } from 'react';

type UseInfiniteScrollProps = {
  loadMore: () => Promise<void>;
  hasMore: boolean;
  isLoading: boolean;
};

const useInfiniteScroll = ({
  loadMore,
  hasMore,
  isLoading
}: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!hasMore) return;

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting) {
        setIsFetching(true);
        loadMore().finally(() => setIsFetching(false));
      }
    };

    observer.current = new IntersectionObserver(handleObserver);
  }, [isLoading, hasMore, loadMore]);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (observer.current && loaderRef.current) {
      observer.current.observe(loaderRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
      if (observer.current && loaderRef.current) {
        observer.current.unobserve(loaderRef.current);
      }
    };
  }, [loaderRef.current]);

  return { loaderRef, isFetching };
};

export default useInfiniteScroll;
